import { Row } from "@/components";
import { Button } from "@/components/ui/button";

import { notificationChannelToLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  NotificationChannel,
  UserNotificationPreferenceResponse,
} from "@/types";

const NOTIFICATION_CHANNELS = Object.values(NotificationChannel);

const isChannelEnabled = (
  preference: UserNotificationPreferenceResponse,
  channel: NotificationChannel,
): boolean => {
  if (channel === NotificationChannel.IN_APP) return preference.inAppEnabled;
  if (channel === NotificationChannel.EMAIL) return preference.emailEnabled;
  return preference.slackEnabled;
};

export const NotificationChannelSelector = ({
  onToggleChannel,
  preference,
  requiredChannel,
  requiresAtLeastOneChannel,
  unavailableChannel = [],
}: {
  onToggleChannel: (channel: NotificationChannel, enabled: boolean) => void;
  preference: UserNotificationPreferenceResponse;
  requiredChannel: NotificationChannel[];
  requiresAtLeastOneChannel: boolean;
  unavailableChannel?: NotificationChannel[];
}) => {
  const enabledCount = NOTIFICATION_CHANNELS.filter((channel) =>
    isChannelEnabled(preference, channel),
  ).length;

  const render = () => (
    <Row className="items-center gap-2">
      {NOTIFICATION_CHANNELS.map((channel) => {
        const isEnabled = isChannelEnabled(preference, channel);
        const isLastEnabledChannel =
          requiresAtLeastOneChannel && isEnabled && enabledCount === 1;
        const isLockedOn = requiredChannel.includes(channel);
        const isDisabled =
          isLockedOn ||
          isLastEnabledChannel ||
          unavailableChannel.includes(channel);

        return (
          <Button
            key={channel}
            className={cn(
              "h-9 min-w-[72px] rounded-full border px-4 text-sm font-medium",
              isEnabled
                ? "border-primary-500 bg-primary-100 font-semibold text-primary-900 hover:bg-primary-200"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              isDisabled && "cursor-not-allowed opacity-80",
            )}
            disabled={isDisabled}
            onClick={() => {
              onToggleChannel(channel, !isEnabled);
            }}
            variant="outlineSecondary"
          >
            {notificationChannelToLabel[channel]}
          </Button>
        );
      })}
    </Row>
  );

  return render();
};
